export const RELEASE_CAMPAIGN_REFRESH_BASE_INTERVAL_MS = 5 * 60_000
export const RELEASE_CAMPAIGN_REFRESH_MAX_JITTER_MS = 30_000
export const DISABLED_RELEASE_CAMPAIGN = Object.freeze({ enabled: false })
export const RELEASE_CAMPAIGN_LOAD_STATUS = Object.freeze({
  ERROR: 'error',
  LOADING: 'loading',
  READY: 'ready'
})
export const RELEASE_CAMPAIGN_COPY_LIMITS = Object.freeze({
  compactLabel: 64,
  ctaLabel: 32,
  title: 120,
  status: 240,
  body: 500
})

const RELEASE_CAMPAIGN_ID_PATTERN = /^[a-z0-9][a-z0-9-]{2,63}$/
const RELEASE_CAMPAIGN_INTERNAL_PATH_PATTERN = /^\/(?!\/)[A-Za-z0-9/_-]*$/
const RELEASE_CAMPAIGN_STAGES = new Set(['alpha', 'beta', 'ga'])
const RELEASE_CAMPAIGN_STATUSES = new Set(['planned', 'active', 'delayed'])

function isRecord(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function isValidPublicText(value, maxLength) {
  return (
    typeof value === 'string' &&
    value.trim().length > 0 &&
    value.length <= maxLength
  )
}

function parseReleaseCampaignDate(releaseDate) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(releaseDate || '')
  if (!match) return null

  const [, yearText, monthText, dayText] = match
  const year = Number(yearText)
  const month = Number(monthText)
  const day = Number(dayText)
  const date = new Date(Date.UTC(year, month - 1, day))

  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null
  }

  return date
}

export function getReleaseCampaignRefreshDelay(randomValue) {
  const boundedRandomValue = Number.isFinite(randomValue)
    ? Math.min(Math.max(randomValue, 0), 1)
    : 0

  return (
    RELEASE_CAMPAIGN_REFRESH_BASE_INTERVAL_MS +
    Math.floor(boundedRandomValue * RELEASE_CAMPAIGN_REFRESH_MAX_JITTER_MS)
  )
}

export function hasValidReleaseCampaignIdentity(campaign) {
  return (
    campaign?.enabled === true &&
    RELEASE_CAMPAIGN_ID_PATTERN.test(campaign.id || '') &&
    RELEASE_CAMPAIGN_STAGES.has(campaign.stage) &&
    RELEASE_CAMPAIGN_STATUSES.has(campaign.status)
  )
}

export function isSafeReleaseCampaignPath(path) {
  return (
    typeof path === 'string' &&
    path.length <= 500 &&
    RELEASE_CAMPAIGN_INTERNAL_PATH_PATTERN.test(path)
  )
}

export function isValidReleaseCampaign(campaign) {
  if (
    !hasValidReleaseCampaignIdentity(campaign) ||
    !parseReleaseCampaignDate(campaign.releaseDate) ||
    !isRecord(campaign.banner) ||
    !isRecord(campaign.statusCopy) ||
    !isRecord(campaign.primaryCta) ||
    !isRecord(campaign.signup) ||
    !isRecord(campaign.pendingApproval) ||
    !isSafeReleaseCampaignPath(campaign.primaryCta.path)
  ) {
    return false
  }

  const copyFields = [
    [campaign.banner.label, RELEASE_CAMPAIGN_COPY_LIMITS.compactLabel],
    [campaign.banner.message, RELEASE_CAMPAIGN_COPY_LIMITS.body],
    [campaign.statusCopy.plannedPrefix, RELEASE_CAMPAIGN_COPY_LIMITS.status],
    [campaign.statusCopy.active, RELEASE_CAMPAIGN_COPY_LIMITS.status],
    [campaign.statusCopy.delayed, RELEASE_CAMPAIGN_COPY_LIMITS.status],
    [campaign.primaryCta.label, RELEASE_CAMPAIGN_COPY_LIMITS.ctaLabel],
    [campaign.signup.title, RELEASE_CAMPAIGN_COPY_LIMITS.title],
    [campaign.signup.description, RELEASE_CAMPAIGN_COPY_LIMITS.body],
    [campaign.signup.submitLabel, RELEASE_CAMPAIGN_COPY_LIMITS.ctaLabel],
    [campaign.pendingApproval.title, RELEASE_CAMPAIGN_COPY_LIMITS.title],
    [campaign.pendingApproval.message, RELEASE_CAMPAIGN_COPY_LIMITS.body]
  ]

  return copyFields.every(([value, maxLength]) =>
    isValidPublicText(value, maxLength)
  )
}

export function normalizeReleaseCampaign(campaign) {
  if (!isValidReleaseCampaign(campaign)) {
    return DISABLED_RELEASE_CAMPAIGN
  }

  return {
    enabled: true,
    id: campaign.id,
    stage: campaign.stage,
    status: campaign.status,
    releaseDate: campaign.releaseDate,
    banner: {
      label: campaign.banner.label,
      message: campaign.banner.message
    },
    statusCopy: {
      plannedPrefix: campaign.statusCopy.plannedPrefix,
      active: campaign.statusCopy.active,
      delayed: campaign.statusCopy.delayed
    },
    primaryCta: {
      label: campaign.primaryCta.label,
      path: campaign.primaryCta.path
    },
    signup: {
      title: campaign.signup.title,
      description: campaign.signup.description,
      submitLabel: campaign.signup.submitLabel
    },
    pendingApproval: {
      title: campaign.pendingApproval.title,
      message: campaign.pendingApproval.message
    }
  }
}

export function formatReleaseCampaignDate(releaseDate) {
  const date = parseReleaseCampaignDate(releaseDate)
  if (!date) return ''

  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC'
  }).format(date)
}
